/**
 * civitaiApi.ts — Service layer for CivitAI checkpoint browsing and install.
 *
 * All CivitAI calls go through the backend proxy (never expose API keys to FE).
 * Uses getApiBaseUrl() + JSON_HEADERS per RULE #16.
 */

import { buildApiUrl } from './urlResolver';
import { JSON_HEADERS, handleDirectResponse } from '../utils/httpUtils';

// ============================================================================
// Types
// ============================================================================

export interface CheckpointInfo {
  slug: string;
  display_name: string;
  filename: string;
  civitai_model_id: number;
  civitai_version_id: number;
  base_model: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0';
  is_hyper: boolean;
  size_mb: number;
  use_cases: string[];
  license_spdx: string;
  license_summary: string;
  license_allows_commercial: boolean;
  nsfw: boolean;
  description: string;
  creator: string;
  civitai_url: string;
  is_installed: boolean;
  is_curated: boolean;
  // CivitAI thumbnail URL, shared with the ComfyUI-SD picker's thumbnail cache
  // infra. null when no image is cached/available.
  image_url?: string | null;
  // Per-model sampler recommendations (mirror ImageModelInfo.recommended_*).
  // null = use Deforum defaults (25 steps, CFG 7.5, euler_ancestral, normal).
  // Populated for Lightning/Hyper checkpoints that need specific settings.
  recommended_steps?: number | null;
  recommended_cfg?: number | null;
  recommended_sampler?: string | null;
  recommended_scheduler?: string | null;
}

export interface CivitAISearchItem {
  id: number;
  name: string;
  base_model: string;
  thumbnail_url: string | null;
  downloads_count: number;
  rating: number | null;
  license_label: string;
  size_mb: number;
  creator: string;
  nsfw: boolean;
  latest_version_id: number;
  tags: string[];
}

export interface CivitAISearchResponse {
  items: CivitAISearchItem[];
  next_cursor: string | null;
}

export interface InstallJobStatus {
  status: 'queued' | 'downloading' | 'verifying' | 'installed' | 'failed';
  progress_mb: number;
  total_mb: number;
  error: string | null;
}

export interface InstallResponse {
  job_id: string;
}

// ============================================================================
// API functions
// ============================================================================

/**
 * List all checkpoints: curated catalog + any user-installed ones.
 */
export async function listInstalledCheckpoints(): Promise<CheckpointInfo[]> {
  const url = buildApiUrl('/api/v1/components/deforum/checkpoints');
  const resp = await fetch(url, {
    method: 'GET',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  return handleDirectResponse<CheckpointInfo[]>(resp);
}

// ============================================================================
// CivitAI search
// ============================================================================

export interface SearchCheckpointsOpts {
  query?: string;
  base_model?: string;
  cursor?: string;
  nsfw?: boolean;
}

/**
 * Search CivitAI via the backend proxy.
 */
export async function searchCivitAICheckpoints(
  opts: SearchCheckpointsOpts
): Promise<CivitAISearchResponse> {
  const params = new URLSearchParams();
  if (opts.query) params.set('query', opts.query);
  if (opts.base_model) params.set('base_model', opts.base_model);
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.nsfw !== undefined) params.set('nsfw', String(opts.nsfw));

  const url = buildApiUrl(`/api/v1/civitai/search?${params.toString()}`);
  const resp = await fetch(url, {
    method: 'GET',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  return handleDirectResponse<CivitAISearchResponse>(resp);
}

// ============================================================================
// Install / status / uninstall
// ============================================================================

export interface InstallCheckpointOpts {
  slug?: string;
  civitai_version_id?: number;
}

/**
 * Trigger download of a checkpoint from CivitAI.
 * Returns a job_id for polling.
 */
export async function installCheckpoint(opts: InstallCheckpointOpts): Promise<InstallResponse> {
  const url = buildApiUrl('/api/v1/components/deforum/checkpoints/install');
  const resp = await fetch(url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(opts),
  });
  return handleDirectResponse<InstallResponse>(resp);
}

/**
 * Poll install job status.
 * Fail loud per RULE #5: caller must handle 'failed' status explicitly.
 */
export async function getCheckpointInstallStatus(job_id: string): Promise<InstallJobStatus> {
  const url = buildApiUrl(
    `/api/v1/components/deforum/checkpoints/install/${encodeURIComponent(job_id)}/status`
  );
  const resp = await fetch(url, {
    method: 'GET',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  return handleDirectResponse<InstallJobStatus>(resp);
}

/**
 * Ask the backend to recommend a checkpoint for the given topic.
 * Returns null when no match is found.
 */
export async function recommendCheckpoint(topic: string): Promise<CheckpointInfo | null> {
  const params = new URLSearchParams({ topic });
  const url = buildApiUrl(`/api/v1/components/deforum/checkpoints/recommend?${params.toString()}`);
  const resp = await fetch(url, {
    method: 'GET',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (resp.status === 204) return null;
  const data = await handleDirectResponse<CheckpointInfo | null>(resp);
  return data;
}
